import useGetMyCustomFieldGroups from '@/api/queries/me/useGetMyCustomFieldGroups';
import {
  CustomFieldType,
  FieldsetCustomFieldDefinition,
} from '@payaca/custom-fields';
import { useMemo } from 'react';

export const useProjectCustomFields = (type?: CustomFieldType[]) => {
  const { data: customFieldGroups, ...rest } = useGetMyCustomFieldGroups();

  const projectFieldGroup = useMemo(() => {
    return customFieldGroups?.find((x) => x.role === 'project');
  }, [customFieldGroups]);

  const projectCustomFields = useMemo(() => {
    return (
      projectFieldGroup?.customFields
        .filter((x) => !x.archivedAt)
        .filter((x) => {
          if (!type || type.length === 0) return true;

          return type.includes(x.type as CustomFieldType);
        }) || []
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'type.length', and 'type'
  }, [projectFieldGroup]);

  return { customFieldGroups, projectFieldGroup, projectCustomFields, ...rest };
};

export const useGetProjectCustomFieldVariables = <T extends string>(
  namePrefix: T,
  displayNamePrefix: string,
  excludeTypes: CustomFieldType[] = []
) => {
  const { projectCustomFields } = useProjectCustomFields();

  const variables = useMemo(() => {
    const v: { variableName: string; displayName: string }[] = [];
    projectCustomFields
      .filter(
        (field) =>
          [
            'text',
            'select',
            'email',
            'textarea',
            'boolean',
            'fieldset',
            'number',
            'url',
          ].includes(field.type) &&
          !field.archivedAt &&
          !(excludeTypes || []).includes(field.schema.type)
      )
      .forEach((field) => {
        if (field.type === 'fieldset') {
          const fieldsetDefinition = new FieldsetCustomFieldDefinition(
            field.schema
          );

          const allChildren = fieldsetDefinition.getChildrenWithPaths();

          // Build a lookup from full path to label for hierarchy display
          const labelByPath = new Map<string, string>();
          for (const { definition, path } of allChildren) {
            labelByPath.set(path.join('.'), definition.label);
          }

          allChildren
            .filter(({ definition }) => definition.type !== 'fieldset')
            .filter(
              ({ definition }) =>
                !(excludeTypes || []).includes(definition.type)
            )
            .forEach(({ path }) => {
              const [root, ...segments] = path;
              const bracketPart = segments.map((seg) => `[${seg}]`).join('');
              const variableName = `${namePrefix}:${root}${bracketPart}`;

              const segmentLabels = path.slice(1).map((_, i) => {
                const progressivePath = path.slice(0, i + 2).join('.');
                return labelByPath.get(progressivePath) ?? path[i + 1];
              });
              const displayName = `${displayNamePrefix}: ${segmentLabels.join(' > ')}`;

              v.push({ variableName, displayName });
            });
        } else {
          const variableName = `${namePrefix}:${field.identifier}`;
          v.push({
            variableName,
            displayName: `${displayNamePrefix}: ${field.identifier}`,
          });
        }
      });
    return v;
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'displayNamePrefix', 'excludeTypes', and 'namePrefix'
  }, [projectCustomFields]);

  return variables;
};
